import React from 'react';
import { css } from '@emotion/react';
import { theme } from 'twin.macro';
import ProductMenu from './ProductMenu';
import config from '$/config';
import Helmet from 'react-helmet';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import UseCasesMenu from './UseCasesMenu';
import ResourcesMenu from './ResourcesMenu';

const logo = require('$/images/logoColor.svg').default;
const redUnderline = require('$/images/redUnderline.svg').default;
const ArrowDown = require('$/images/arrow-down.svg').default;

const Logo: React.FC<{ url: string }> = ({ url }) => (
  <div className='mx-auto md:mx-0'>
    <a href={url}>
      <img src={logo} className={`logoImage`} alt='logo' />
    </a>
  </div>
);

type MarketingHeaderMenu =
  | 'product'
  | 'usecases'
  | 'resources'
  | 'pricing'
  | 'joindemo'
  | 'login'
  | 'signup'
  | 'cash';

interface MenuLink {
  name: string;
  id: MarketingHeaderMenu;
  to: string;
  target?: string;
  hasSubMenu?: boolean;
  color?: string;
  className?: string;
}

const NavLink: React.FC<{ link: MenuLink; isLg: boolean; isLoggedIn?: boolean }> = ({
  link,
  isLg,
  isLoggedIn,
}) => {
  const [activeSubmenu, setActiveSubmenu] = React.useState(false);

  return (
    <span
      className={`relative ${link.hasSubMenu ? 'has-submenu' : ''} `}
      css={
        !isLg &&
        activeSubmenu &&
        css`
          z-index: 1;
          &:hover {
            > a {
              color: black !important;
              font-weight: 700 !important;
            }
          }
          .menu-sub {
            display: block !important;
          }
        `
      }
      onMouseLeave={(): void => {
        if (!isLg && link.hasSubMenu) {
          setTimeout(() => {
            setActiveSubmenu(false);
          }, 0);
        }
      }}
    >
      <a
        className={`nav-link items-center nav-link flex px-4 md:px-2 text-lg ${link.className} ${
          isLoggedIn ? 'md:text-md' : 'md:text-sm'
        } ${link.color ? link.color : 'dark-color'}`}
        href={link.to}
        key={link.name}
        target={link.target ?? isLoggedIn ? '_blank' : '_self'}
        onClick={(e): void => {
          link.hasSubMenu && e.preventDefault();
          !isLg && setActiveSubmenu(!activeSubmenu);
        }}
      >
        {link.name}
        {link.hasSubMenu && (
          <img
            className={`arrowIcon transition-100 h-full ${isLoggedIn ? 'w-3 ml-3' : 'w-2 ml-2'}`}
            css={
              activeSubmenu &&
              css`
                transform: rotate(180deg);
              `
            }
            src={ArrowDown}
            alt='Arrow Down'
          />
        )}
      </a>
      {link.name === 'Product' && <ProductMenu target={isLoggedIn ? '_blank' : '_self'} />}
      {link.name === 'Use Cases' && <UseCasesMenu target={isLoggedIn ? '_blank' : '_self'} />}
      {link.name === 'resources' && <ResourcesMenu target={isLoggedIn ? '_blank' : '_self'} />}
    </span>
  );
};

export const MarketingHeader: React.FC<{
  isLoggedIn?: boolean;
  show?: (MarketingHeaderMenu | undefined)[];
  hide?: (MarketingHeaderMenu | undefined)[];
}> = ({ isLoggedIn, show, hide }) => {
  const linkContainerRef = React.useRef<HTMLDivElement>(null);
  const { height: heightBody, lg: isLg } = useWindowSizeInfo();
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!show?.includes('cash')) {
    hide = hide?.concat(['cash']) ?? ['cash'];
  }

  const navLinks: MenuLink[] = [
    { name: 'Product', id: 'product', to: '', hasSubMenu: true },
    { name: 'Use Cases', id: 'usecases', to: '', hasSubMenu: true },
    { name: 'resources', id: 'resources', to: '', hasSubMenu: true },
    { name: 'pricing', id: 'pricing', to: `${config.URL_MARKETING_SITE}/pricing`, color: '' },
    {
      name: 'join a demo',
      id: 'joindemo',
      to: `https://ilove.ewebinar.com/webinar/15-minute-ewebinar-walkthrough-and-demo-150`,
      color: '#39a1b2',
    },
    {
      name: 'Earn $100 cash!',
      id: 'cash',
      to: '/home/profile/rewards',
      target: '',
      className: 'normal-case italic',
      color: '#39a1b2',
    },
  ];

  const help = [
    {
      title: 'Contact',
      link: `${config.URL_MARKETING_SITE}/contact`,
      showModal: false,
      color: '',
    },
  ];

  const toggleMenu = (): void => {
    if (linkContainerRef.current) {
      setIsExpanded(!isExpanded);
    }
  };

  const doShow = (menu?: MarketingHeaderMenu): boolean => {
    if (!menu) return true;
    return Boolean((!show || show.includes(menu)) && !hide?.includes(menu));
  };

  return (
    <div
      className={`headerContainer fixed`}
      id='fixedHeader'
      css={css`
        background-color: #fff;
        z-index: 100000;
        top: 0;
        width: ${isLoggedIn ? 'auto' : '100%'};
        box-shadow: ${isLoggedIn ? 'none' : '0 0 3px rgba(0, 0, 0, 0.2)'};
        .menu-sub {
          box-shadow: 0 0 2px rgb(0, 0, 0, 0.3);
          top: 3rem;
          @media (max-width: 767px) {
            width: 100%;
          }
          .itemSubmenu {
            img {
              filter: grayscale(100%);
              width: 100%;
            }
            &:hover {
              img {
                filter: grayscale(0%);
              }
            }
          }
          h6 {
            min-width: 6rem;
          }
          .col-sub {
            h6 {
              font-size: 16px;
              font-weight: 700;
            }
          }
          @media (max-width: 640px) {
            width: 100%;
            .col-sub {
              h6 {
                font-size: 14px;
                font-weight: 300;
              }
            }
          }
          .description {
            font-size: 13px;
            font-weight: 200;
          }
        }
        .color-primary {
          color: ${theme`colors.brand.teal.light`} !important;
        }
        .dark-color {
          color: ${theme`colors.gray.28`} !important;
        }
        .header {
          padding: 9px 0;
          @media (max-width: 767px) {
            padding: 0;
            html {
              font-size: initial;
            }
          }
          flex-direction: column;
          max-width: 100%;
          & :global(.navbar) {
            padding-left: 0px;
            padding-right: 0px;
          }
          & :global(.navbar-brand) {
            flex-grow: 2;
          }
          & :global(.navbar-toggler) {
            border-color: transparent !important;
          }
        }

        .logoImageContainer {
          margin: auto;
          text-align: center;
        }

        .logoImage {
          width: 156px;
          position: relative;
          margin: auto;
          max-width: initial;
        }

        .menuIcon {
          height: 30px;
          width: 30px;
          display: none;
        }

        .linkContainer {
          justify-content: center;
          text-align: center;
          transition: color 200ms ease-out;
          display: none;
          height: calc(${heightBody}px - 54px);
          &.active {
            display: block !important;
          }
          span > a {
            font-weight: 700;
            line-height: 50px;
            padding-right: 0.5rem;
            padding-left: 0.5rem;
            text-transform: uppercase;
            font-size: 18px;
            &:hover {
              color: ${theme`colors.brand.teal.dark`};
              text-decoration: none;
            }
          }
          @media (min-width: 992px) {
            display: flex;
            height: auto;
            span > a {
              margin-right: 10px;
              line-height: 50px;
              font-weight: 700;
              text-transform: uppercase;
              transition: color 300ms;
            }
          }
        }
        .authLinkContainer {
          margin: 10px 0;

          & .signin {
            color: black;
            text-transform: capitalize;
            font-size: 15px;
            line-height: normal;
            font-weight: 500;
            margin: 0 !important;
          }
          @media (max-width: 767px) {
            & .signin {
              font-weight: 300;
            }
          }
          & .signup {
            color: ${theme`colors.coral.1`} !important;
            position: relative;
            font-size: 15px;
            line-height: 19px;
            opacity: 1;
            font-weight: 700;
            text-transform: initial;
            padding: 0.574rem 1rem;
            margin: 0 !important;
            &::after {
              content: '';
              background-image: url(${redUnderline});
              background-repeat: no-repeat;
              height: 10px;
              position: absolute;
              bottom: -6px;
              width: 100%;
              display: inline-block;
              cursor: pointer;
              left: 0px;
              background-position: center;
              background-size: 100% 100%;
            }

            &:hover {
              color: ${theme`colors.coral.1`};
              opacity: 0.8;
            }
          }
        }

        @media (min-width: ${isLoggedIn ? '768px' : '1024px'}) {
          .header {
            max-width: 1200px;
            padding-left: 30px;
            padding-right: 30px;
            flex-direction: row;
          }
          .linkContainer {
            display: flex;
            margin-top: unset;
            flex-direction: row;
            height: 100%;
          }
        }
        @media (max-width: 992px) {
          .menuIcon {
            display: block;
          }
          .authLinkContainer {
            margin: 0;
            & .signup {
              font-size: 18px;
              margin: 0 auto;
              line-height: 26px;
              width: max-content;
              padding: 0.5rem 0rem;
              &:after {
                background-size: 100% 120%;
              }
            }
            .signin {
              font-size: 18px;
              margin-bottom: 20px;
              line-height: 26px;
              padding: 0.5rem 0rem;
            }
          }
        }
      `}
    >
      <Helmet>
        <style>
          {!isLoggedIn &&
            `
            html{
              font-size: initial;
            }
          `}
        </style>
      </Helmet>

      {/* the header width is wider than the page */}
      <div className={`header flex items-center mx-auto ${isLoggedIn && 'p-0'}`}>
        <div
          className='flex justify-between w-full md:w-auto items-center'
          css={css`
            @media only screen and (max-width: 992px) {
              padding: 12px 0;
            }
          `}
        >
          <Logo url={isLoggedIn ? '/home' : config.URL_MARKETING_SITE} />

          <div
            className={`menuIcon ${isLoggedIn && 'hidden'} ${isExpanded && 'opened'}`}
            onClick={toggleMenu}
            css={css`
              #nav-icon2 {
                width: 60px;
                height: 45px;
                position: absolute;
                top: 5.5px;
                right: 0;

                -webkit-transform: rotate(0deg);
                -moz-transform: rotate(0deg);
                -o-transform: rotate(0deg);
                transform: rotate(0deg) scale(0.4);
                -webkit-transition: 0.5s ease-in-out;
                -moz-transition: 0.5s ease-in-out;
                -o-transition: 0.5s ease-in-out;
                transition: 0.5s ease-in-out;
                cursor: pointer;
              }
              #nav-icon2 span {
                display: block;
                position: absolute;
                height: 9px;
                width: 50%;
                background: #000;
                opacity: 1;
                -webkit-transform: rotate(0deg);
                -moz-transform: rotate(0deg);
                -o-transform: rotate(0deg);
                transform: rotate(0deg);
                -webkit-transition: 0.25s ease-in-out;
                -moz-transition: 0.25s ease-in-out;
                -o-transition: 0.25s ease-in-out;
                transition: 0.25s ease-in-out;
              }

              #nav-icon2 span:nth-of-type(even) {
                left: 50%;
                border-radius: 0 9px 9px 0;
              }

              #nav-icon2 span:nth-of-type(odd) {
                left: 0px;
                border-radius: 9px 0 0 9px;
              }

              #nav-icon2 span:nth-of-type(1),
              #nav-icon2 span:nth-of-type(2) {
                top: 0px;
              }

              #nav-icon2 span:nth-of-type(3),
              #nav-icon2 span:nth-of-type(4) {
                top: 18px;
              }

              #nav-icon2 span:nth-of-type(5),
              #nav-icon2 span:nth-of-type(6) {
                top: 36px;
              }

              &.opened #nav-icon2 span:nth-of-type(1),
              &.opened #nav-icon2 span:nth-of-type(6) {
                -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg);
                transform: rotate(45deg);
              }

              &.opened #nav-icon2 span:nth-of-type(2),
              &.opened #nav-icon2 span:nth-of-type(5) {
                -webkit-transform: rotate(-45deg);
                -moz-transform: rotate(-45deg);
                -o-transform: rotate(-45deg);
                transform: rotate(-45deg);
              }

              &.opened #nav-icon2 span:nth-of-type(1) {
                left: 5px;
                top: 7px;
              }

              &.opened #nav-icon2 span:nth-of-type(2) {
                left: calc(50% - 5px);
                top: 7px;
              }

              &.opened #nav-icon2 span:nth-of-type(3) {
                left: -50%;
                opacity: 0;
              }

              &.opened #nav-icon2 span:nth-of-type(4) {
                left: 100%;
                opacity: 0;
              }

              &.opened #nav-icon2 span:nth-of-type(5) {
                left: 5px;
                top: 29px;
              }

              &.opened #nav-icon2 span:nth-of-type(6) {
                left: calc(50% - 5px);
                top: 29px;
              }
            `}
          >
            <div id='nav-icon2'>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <div
          className={`
            pb-5 md:pb-0 linkContainer w-full items-center ${isExpanded && 'active'}`}
          css={css`
            @media only screen and (max-width: 767px) {
              overflow-y: scroll;
            }
          `}
          ref={linkContainerRef}
        >
          <div
            className={`flex flex-col ${
              isLoggedIn ? 'md:flex-row md:ml-8' : 'lg:flex-row lg:ml-8'
            }`}
            css={css`
              margin-top: 5px;
              @media only screen and (max-width: 991px) {
                margin-top: ${isLoggedIn ? '5px' : '20px'};
              }
              ${isLg &&
              `.has-submenu:hover {
                z-index: 1;
                > a {
                  color: black !important;
                  font-weight: 700 !important;
                }
                .menu-sub {
                  display: block !important;
                }
                .arrowIcon {
                  transform: rotate(180deg);
                }
              }`}
            `}
          >
            {navLinks.map((link, index) => {
              if (!doShow(link.id)) return null;

              return <NavLink key={index} link={link} isLg={isLg} isLoggedIn={isLoggedIn} />;
            })}
            {help.map((ele, index) => (
              <span className={`relative`} key={index}>
                <a
                  href={ele.link}
                  key={index}
                  className={`nav-link md:hidden items-center nav-link flex px-4 text-lg md:text-sm md:px-2 ${
                    ele.color ? ele.color : 'dark-color'
                  }`}
                >
                  {ele.title}
                </a>
              </span>
            ))}
          </div>
          <div
            className={`authLinkContainer px-4 md:px-2 lg:px-0 ml-auto flex flex-col lg:flex-row overflow-visible lg:items-center ${
              isLoggedIn && 'hidden'
            }`}
          >
            <a
              className={`${!doShow('login') && 'invisible'} signin dark-color text-left`}
              href='/login'
            >
              Login
            </a>
            <a href='/signup' className={`${!doShow('signup') && 'invisible'} signup relative`}>
              Sign up free
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
